import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Category item for tabs
 */
export interface CategoryItem {
  id: string;
  name: string;
  slug: string;
  count?: number;
}

/**
 * Legacy type for backwards compatibility
 * @deprecated Use CategoryItem instead
 */
export type ProductCategory = string;

/**
 * Default fallback categories if none provided
 */
/** The synthetic "all categories" tab every category list starts with. */
export function allCategoryEntry(t: TFunction): CategoryItem {
  return {
    id: 'all',
    name: t('creator.catalog.defaultCategories.all'),
    slug: 'all',
  };
}

function defaultCategories(t: TFunction): CategoryItem[] {
  return [
    allCategoryEntry(t),
    {
      id: 'unisex-shirts',
      name: t('creator.catalog.defaultCategories.clothing'),
      slug: 'unisex-shirts',
    },
    {
      id: 'accessories',
      name: t('creator.catalog.defaultCategories.accessories'),
      slug: 'accessories',
    },
    {
      id: 'home-living',
      name: t('creator.catalog.defaultCategories.homeLiving'),
      slug: 'home-living',
    },
  ];
}

interface CategoryTabsProps {
  /** Dynamic categories from API */
  categories?: CategoryItem[];
  /** Currently active category slug */
  activeCategory: string;
  /** Callback when category changes */
  onCategoryChange: (categorySlug: string) => void;
}

/**
 * Scrollable & swipeable category tabs with arrow navigation (JUNIQE style)
 * Supports: Mouse scroll, Touch swipe, Arrow buttons
 * Now accepts dynamic categories from API
 */
export function CategoryTabs({
  categories: providedCategories,
  activeCategory,
  onCategoryChange,
}: CategoryTabsProps) {
  const { t } = useTranslation();
  const fallbackCategories = useMemo(() => defaultCategories(t), [t]);
  const categories = providedCategories ?? fallbackCategories;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Touch/swipe state
  const touchStartX = useRef<number>(0);
  const isDragging = useRef<boolean>(false);

  // Check scroll position to show/hide arrows
  const updateScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, [updateScrollButtons]);

  // Scroll left/right by amount
  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 120;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  // Touch event handlers for swipe support
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    isDragging.current = true;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!isDragging.current || !touch) return;
    const touchEndX = touch.clientX;

    // Calculate drag distance and scroll
    const container = scrollContainerRef.current;
    if (!container) return;

    const diff = touchStartX.current - touchEndX;
    container.scrollLeft += diff * 0.5;
    touchStartX.current = touchEndX;
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
  };

  // Mouse drag support for desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    touchStartX.current = e.clientX;
    isDragging.current = true;
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const diff = touchStartX.current - e.clientX;
    container.scrollLeft += diff;
    touchStartX.current = e.clientX;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  return (
    <div className="relative border-b border-[var(--line)] bg-[var(--paper)]">
      {/* Left scroll button */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-[var(--paper)] via-[var(--paper)] to-transparent flex items-center justify-start pl-1"
          aria-label={t('creator.catalog.scrollLeft')}
        >
          <ChevronLeft className="w-4 h-4 text-[var(--mute)]" />
        </button>
      )}

      {/* Scrollable & swipeable tabs container */}
      <div
        ref={scrollContainerRef}
        onScroll={updateScrollButtons}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className={cn(
          'flex overflow-x-auto scrollbar-hide select-none',
          isDragging.current && 'cursor-grabbing'
        )}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          cursor: 'grab',
        }}
      >
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => onCategoryChange(category.slug)}
            className={cn(
              'px-4 py-3 font-sans font-semibold text-sm whitespace-nowrap transition-colors shrink-0',
              activeCategory === category.slug
                ? 'text-[var(--ink)] shadow-[inset_0_-2px_0_rgb(var(--color-primary-500))]'
                : 'text-[var(--mute)] hover:text-[var(--ink)]'
            )}
          >
            {category.name}
            {category.count !== undefined && (
              <span className="ml-1 text-xs text-[var(--mute)]">
                ({category.count})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Right scroll button */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-[var(--paper)] via-[var(--paper)] to-transparent flex items-center justify-end pr-1"
          aria-label={t('creator.catalog.scrollRight')}
        >
          <ChevronRight className="w-4 h-4 text-[var(--mute)]" />
        </button>
      )}
    </div>
  );
}
